import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";

import { GetDueFeeReport } from "../../api/HostelService";
import { getSessions } from "../../api/SessionsService";
import { ToastMain, SweetAlertShow, LoadingDiv } from "../master/Master";
import { StaffList } from "../../api/Staff";

import HostelHeader from "./HostelHeader";
import Nav from "../master/NavBar";
import WelcomeBar from "./WelcomeBar";
import usePrivateLink from "../config/usePrivateLink";
import HostelReportTabs from "./HostelReportTabs";

export default function HostelFeeDue() {
  const [page, setPage] = useState(1);
  const [Data, setData] = useState(null);
  const [total, setTotal] = useState(0);

  const [staffs, setStaffs] = useState([]);
  const [name, setName] = useState("");
  const [staffId, setStaffId] = useState("");

  const [loading, setLoading] = useState(false);
  const [selectedSession, setSelectedSessions] = useState("");
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState("");
  const [transition, setTransition] = useState([]);
  const [type, setType] = useState("");

  const [hostel_id, setHostelId] = useState(localStorage.getItem("hostel_id"));

  const hasAccess = usePrivateLink();

  /* =========================================================
     DEFAULT DATES
  ========================================================= */

  const getDefaultDates = () => {
    const today = new Date();

    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const formatDate = (date) => {
      if (!(date instanceof Date)) date = new Date(date);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    };

    return {
      from: formatDate(firstDayOfMonth),
      to: formatDate(today),
    };
  };

  const { from: defaultFrom, to: defaultTo } = getDefaultDates();

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  /* =========================================================
     SESSION
  ========================================================= */

  const getSession = async () => {
    setLoading(true);

    try {
      const response = await getSessions();

      if (response?.data?.status) {
        const activeSession = response?.data?.data.find(
          (session) => session.status === "1",
        );

        setSelectedSessions(activeSession.id);
        setSessions(response?.data?.data);
      } else {
        setError("Failed to fetch session.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setHostelId(localStorage.getItem("hostel_id"));
  }, []);

  useEffect(() => {
    getSession();
  }, []);

  /* =========================================================
     EXPORT
     BATCH FILTER REMOVED
  ========================================================= */

  const handleExportAll = async () => {
    try {
      setLoading(true);

      let addUrl = `&page=${page}&session_id=${selectedSession}`;

      if (from) addUrl += `&min_amount=${from}`;
      if (to) addUrl += `&max_amount=${to}`;
      if (name) addUrl += `&search=${name}`;
      if (hostel_id) addUrl += `&hostel_id=${hostel_id}`;

      const firstResponse = await GetDueFeeReport(`${addUrl}&page=1`);

      let allData = Array.isArray(firstResponse?.data?.data?.data)
        ? firstResponse.data.data.data
        : [];

      const lastPage = firstResponse?.data?.data?.last_page || 1;

      for (let p = 2; p <= lastPage; p++) {
        const response = await GetDueFeeReport(`${addUrl}&page=${p}`);

        const records = Array.isArray(response?.data?.data?.data)
          ? response.data.data.data
          : [];

        allData.push(...records);
      }

      if (!allData.length) {
        alert("No data to export!");
        setLoading(false);
        return;
      }

      const formattedData = allData.map((item, index) => {
        const hs = item.hostel_student || {};
        const hostel = hs.hostel || {};
        const student = hs.student || {};
        const room = hs.room || {};
        const bed = hs.bed || {};

        return {
          "Sr. No": index + 1,
          "Hostel Name": hostel.name || "",
          "Hostel Address": hostel.address || "",
          "Hostel Mobile": hostel.mobile || "",
          "Student Name": student.name || hs.student_name || "",
          "Father Name": student.father_name || hs.father_name || "",
          "Mobile Number": student.phone || hs.mobile || "",
          "Aadhar No": hs.aadhar_no || "",
          "Room No": room.room_no || "",
          "Bed No": bed.bed_no || "",
          "Bed Charge (₹)": bed.bed_charge || "",
          Month: item.month || "",
          "Total Amount (₹)": item.total_amount || 0,
          "Final Amount (₹)": item.final_amount || 0,
          "Paid Amount (₹)": item.paid_amount || 0,
          "Left Amount (₹)": item.left_amount || 0,
          "Next Payment Date": item.next_payment_date || "",
          "Joining Date": hs.joining_date || "",
          "Student Address": hs.address || "",
          Village: student.village || "",
          District: student.district || "",
          State: student.state || "",
          "Admission Date": student.admission_date || "",
        };
      });

      const workbook = XLSX.utils.book_new();

      const dueSheet = XLSX.utils.json_to_sheet(formattedData);

      XLSX.utils.book_append_sheet(workbook, dueSheet, "Hostel Due Fee Report");

      const totalSum = firstResponse?.data?.total_sum || 0;

      const totalRow = [
        {
          "Hostel Name": "Total Left Amount (₹)",
          "Left Amount (₹)": totalSum,
        },
      ];

      XLSX.utils.sheet_add_json(dueSheet, totalRow, {
        origin: -1,
        skipHeader: true,
      });

      XLSX.writeFile(workbook, "HostelDueFeeReport.xlsx");

      ToastMain.fire({
        icon: "success",
        title: `Exported ${allData.length} due fee records successfully!`,
      });
    } catch (error) {
      console.error(error);

      SweetAlertShow("error", "Error", error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOnce = useRef(false);

  /* =========================================================
     FETCH DATA
     BATCH FILTER REMOVED
  ========================================================= */

  const fetchData = async () => {
    setLoading(true);

    let addUrl = `&page=${page}&session_id=${selectedSession}`;

    if (from) addUrl += `&min_amount=${from}`;
    if (to) addUrl += `&max_amount=${to}`;
    if (name) addUrl += `&search=${name}`;
    if (hostel_id) addUrl += `&hostel_id=${hostel_id}`;

    try {
      const response = await GetDueFeeReport(addUrl);

      setData(response?.data?.data);
      setTransition(response?.data?.data?.data || []);
      setTotal(response?.data?.total_sum || 0);
    } catch (error) {
      console.error("Failed to fetch due fee data:", error);
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     STAFF
  ========================================================= */

  const getStaffs = async () => {
    setLoading(true);

    try {
      let response = await StaffList();

      setStaffs(response?.data?.batches);
    } catch (error) {
      console.error("Failed to fetch chart data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStaffs();
  }, []);

  /* =========================================================
     FETCH REPORT
  ========================================================= */

  useEffect(() => {
    fetchData();
  }, [page, from, to, staffId, name, selectedSession, type]);

  /* =========================================================
     UI HELPERS
  ========================================================= */

  const formatAmount = (amount) =>
    Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatDate = (date) => {
    if (!date) return "-";

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) return "-";

    return parsed.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getCurrentSessionName = () => {
    const session = sessions?.find(
      (item) => String(item?.id) === String(selectedSession),
    );

    return session?.session || "-";
  };

  return (
    <>
      {/* =========================================================
          LOADING OVERLAY
      ========================================================= */}

      {loading && <LoadingDiv />}

      {/* =========================================================
          LAYOUT
      ========================================================= */}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <HostelHeader />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">
                {/* <WelcomeBar /> */}

                {/* =================================================
                    PAGE HEADER
                ================================================= */}

                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-3 bg-label-warning"
                      style={{
                        width: 48,
                        height: 48,
                      }}
                    >
                      <i className="ri-money-rupee-circle-line fs-4 text-warning" />
                    </div>

                    <div>
                      <h4 className="mb-1 fw-bold">Due Fee Report</h4>

                      <p className="mb-0 text-muted">
                        Track outstanding hostel fees and student payment dues
                      </p>
                    </div>
                  </div>

                  {/* <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-label-primary px-3 py-2">
                      <i className="ri-calendar-line me-1" />
                      {getCurrentSessionName()}
                    </span>
                  </div> */}
                </div>

                {/* =================================================
                    REPORT NAVIGATION
                ================================================= */}
                <HostelReportTabs />
                {/* <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body p-2">
                    <div className="d-flex flex-wrap gap-2">

                      <div className="ms-lg-auto">
                        <div
                          className="input-group input-group-sm"
                          style={{
                            minWidth: 210,
                          }}
                        >
                          <span className="input-group-text">
                            <i className="ri-calendar-check-line" />
                          </span>

                          <select
                            className="form-select"
                            name="session"
                            value={selectedSession}
                            onChange={(e) =>
                              setSelectedSessions(e.target.value)
                            }
                          >
                            {sessions?.map((session) => (
                              <option key={session?.id} value={session?.id}>
                                {session?.session}
                                {" - "}
                                {session?.status == "1" ? "Active" : "Inactive"}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}

                {/* =================================================
                    FILTER CARD
                ================================================= */}

                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-header border-bottom">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-2 bg-label-primary d-flex align-items-center justify-content-center"
                        style={{
                          width: 38,
                          height: 38,
                        }}
                      >
                        <i className="ri-filter-3-line text-primary fs-5" />
                      </div>

                      <div>
                        <h5 className="mb-0">Filter Report</h5>

                        <small className="text-muted">
                          Search outstanding fee records
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="row g-3">
                      {/* STUDENT SEARCH */}

                      <div className="col-md-8 col-xl-6">
                        <label className="form-label fw-semibold">
                          Student Search
                        </label>

                        <div className="input-group input-group-merge">
                          <span className="input-group-text">
                            <i className="ri-search-line" />
                          </span>

                          <input
                            type="text"
                            className="form-control"
                            name="search"
                            placeholder="Name, mobile or registration no."
                            value={name}
                            onChange={(e) => {
                              setName(e.target.value);
                              setPage(1);
                            }}
                          />

                          {name && (
                            <button
                              type="button"
                              className="btn btn-text-secondary"
                              onClick={() => {
                                setName("");
                                setPage(1);
                              }}
                            >
                              <i className="ri-close-line" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* CURRENT FILTER */}

                      <div className="col-md-4 col-xl-6">
                        <label className="form-label fw-semibold">
                          Current Filter
                        </label>

                        <div
                          className="rounded-3 bg-label-primary d-flex align-items-center px-3"
                          style={{
                            minHeight: 44,
                          }}
                        >
                          <i className="ri-filter-line text-primary me-2" />

                          <span className="text-primary fw-semibold text-truncate">
                            {name ? `Search: "${name}"` : "All Students"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    SUMMARY
                ================================================= */}

                <div className="row g-3 mb-4">
                  {/* TOTAL DUE */}

                  <div className="col-md-6 col-xl-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted">
                              Total Pending Amount
                            </small>

                            <h4 className="mb-0 mt-1 fw-bold">
                              ₹{formatAmount(total)}
                            </h4>
                          </div>

                          <div className="rounded-3 bg-label-warning p-3">
                            <i className="ri-error-warning-line fs-4 text-warning" />
                          </div>
                        </div>

                        <div className="mt-3">
                          <small className="text-muted">
                            Outstanding fee across matching records
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* RECORDS */}

                  <div className="col-md-6 col-xl-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted">
                              Current Page Records
                            </small>

                            <h4 className="mb-0 mt-1 fw-bold">
                              {Data?.total || transition?.length || 0}
                            </h4>
                          </div>

                          <div className="rounded-3 bg-label-primary p-3">
                            <i className="ri-file-list-3-line fs-4 text-primary" />
                          </div>
                        </div>

                        <div className="mt-3">
                          <small className="text-muted">
                            Due fee records matching current filters
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* EXPORT */}

                  <div className="col-md-12 col-xl-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted">Report Export</small>

                            <h6 className="mb-1 mt-1 fw-bold">
                              Download Excel Report
                            </h6>

                            <small className="text-muted">
                              Export all matching records
                            </small>
                          </div>

                          {hasAccess("/hostel/credit/debit/report/export") && (
                            <button
                              type="button"
                              className="btn btn-success d-flex align-items-center gap-2"
                              onClick={handleExportAll}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Export Excel"
                            >
                              <i className="ri-file-excel-2-line fs-5" />
                              Export
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    REPORT TABLE
                ================================================= */}

                <div className="card border-0 shadow-sm">
                  <div className="card-header border-bottom py-3">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                      <div>
                        <div className="d-flex align-items-center gap-2">
                          <h5 className="mb-0 fw-bold">Due Fee Records</h5>

                          <span className="badge bg-label-warning">
                            Pending
                          </span>
                        </div>

                        <small className="text-muted">
                          Student-wise outstanding fee details
                        </small>
                      </div>

                      <div className="text-muted small">
                        Session: <strong>{getCurrentSessionName()}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="card-body p-0">
                    {Data?.data?.length > 0 ? (
                      <div
                        className="table-responsive"
                        style={{
                          maxHeight: "65vh",
                          overflow: "auto",
                        }}
                      >
                        <table className="table table-hover align-middle mb-0">
                          <thead
                            className="table-light"
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 5,
                            }}
                          >
                            <tr className="text-center">
                              <th style={{ minWidth: 60 }}>#</th>

                              <th
                                className="text-start"
                                style={{ minWidth: 220 }}
                              >
                                Student
                              </th>

                              <th style={{ minWidth: 130 }}>Mobile</th>

                              <th style={{ minWidth: 150 }}>Hostel</th>

                              <th style={{ minWidth: 90 }}>Room</th>

                              <th style={{ minWidth: 90 }}>Bed</th>

                              <th style={{ minWidth: 140 }}>Final Amount</th>

                              <th style={{ minWidth: 140 }}>Paid Amount</th>

                              <th style={{ minWidth: 150 }}>Pending Amount</th>

                              <th style={{ minWidth: 110 }}>Month</th>

                              <th style={{ minWidth: 130 }}>Joining Date</th>
                            </tr>
                          </thead>

                          <tbody>
                            {Data?.data?.map((item, i) => {
                              const hs = item.hostel_student || {};
                              const student = hs.student || {};
                              const hostel = hs.hostel || {};
                              const room = hs.room || {};
                              const bed = hs.bed || {};

                              const finalAmount = Number(
                                item?.final_amount || 0,
                              );

                              const paidAmount = Number(item?.paid_amount || 0);

                              const pendingAmount = Number(
                                item?.left_amount || 0,
                              );

                              return (
                                <tr key={item.id || i}>
                                  {/* INDEX */}

                                  <td className="text-center">
                                    <span
                                      className="d-inline-flex align-items-center justify-content-center rounded-circle bg-label-secondary fw-semibold"
                                      style={{
                                        width: 30,
                                        height: 30,
                                      }}
                                    >
                                      {i + 1}
                                    </span>
                                  </td>

                                  {/* STUDENT */}

                                  <td>
                                    <div className="d-flex align-items-center gap-3">
                                      <div
                                        className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{
                                          width: 40,
                                          height: 40,
                                        }}
                                      >
                                        <i className="ri-user-line text-primary fs-5" />
                                      </div>

                                      <div>
                                        <div className="fw-semibold">
                                          {student?.name ||
                                            hs.student_name ||
                                            "-"}
                                        </div>

                                        <small className="text-muted">
                                          {student?.registration_no ||
                                          hs?.registration_no
                                            ? `Reg. No: ${
                                                student?.registration_no ||
                                                hs?.registration_no
                                              }`
                                            : "Registration number unavailable"}
                                        </small>
                                      </div>
                                    </div>
                                  </td>

                                  {/* MOBILE */}

                                  <td className="text-center">
                                    <span className="fw-medium">
                                      {student?.phone || hs.mobile || "-"}
                                    </span>
                                  </td>

                                  {/* HOSTEL */}

                                  <td className="text-center">
                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                      <i className="ri-building-line text-muted" />

                                      <span>{hostel?.name || "-"}</span>
                                    </div>
                                  </td>

                                  {/* ROOM */}

                                  <td className="text-center">
                                    <span className="badge bg-label-primary px-3 py-2">
                                      {room?.room_no || "-"}
                                    </span>
                                  </td>

                                  {/* BED */}

                                  <td className="text-center">
                                    <span className="badge bg-label-info px-3 py-2">
                                      {bed?.bed_no || "-"}
                                    </span>
                                  </td>

                                  {/* FINAL */}

                                  <td className="text-end">
                                    <span className="fw-semibold">
                                      ₹{formatAmount(finalAmount)}
                                    </span>
                                  </td>

                                  {/* PAID */}

                                  <td className="text-end">
                                    <span className="text-success fw-semibold">
                                      ₹{formatAmount(paidAmount)}
                                    </span>
                                  </td>

                                  {/* PENDING */}

                                  <td className="text-end">
                                    <span className="badge bg-label-warning px-3 py-2 fw-bold">
                                      ₹{formatAmount(pendingAmount)}
                                    </span>
                                  </td>

                                  {/* MONTH */}

                                  <td className="text-center">
                                    <span className="badge bg-label-secondary px-3 py-2">
                                      <i className="ri-calendar-line me-1" />
                                      {item?.month || "-"}
                                    </span>
                                  </td>

                                  {/* JOINING */}

                                  <td className="text-center">
                                    <span className="text-muted">
                                      {formatDate(hs.joining_date)}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-5 px-3">
                        <div
                          className="rounded-circle bg-label-secondary d-flex align-items-center justify-content-center mx-auto mb-3"
                          style={{
                            width: 72,
                            height: 72,
                          }}
                        >
                          <i className="ri-file-search-line fs-2 text-muted" />
                        </div>

                        <h5 className="fw-bold mb-2">No Due Fee Records</h5>

                        <p className="text-muted mb-0">
                          No outstanding fee records were found for the selected
                          filters.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* =================================================
                      PAGINATION
                  ================================================= */}

                  {Data?.links?.length > 0 && (
                    <div className="card-footer border-top">
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                        <div>
                          <small className="text-muted">
                            Showing <strong>{Data?.from || 0}</strong> to{" "}
                            <strong>{Data?.to || 0}</strong> of{" "}
                            <strong>{Data?.total || 0}</strong> entries
                          </small>
                        </div>

                        <nav aria-label="Page navigation">
                          <ul className="pagination pagination-sm mb-0">
                            {Data?.links?.map((btn, i) => (
                              <li
                                key={`${btn?.label}-${i}`}
                                className={`page-item ${
                                  btn?.active ? "active" : ""
                                } ${btn?.url ? "" : "disabled"}`}
                              >
                                <button
                                  type="button"
                                  className="page-link waves-effect"
                                  onClick={() => {
                                    if (Data?.links?.length === i + 1) {
                                      btn?.url &&
                                        setPage(Data?.current_page + 1);
                                    } else if (i === 0) {
                                      btn?.url &&
                                        setPage(Data?.current_page - 1);
                                    } else {
                                      setPage(btn.label);
                                    }
                                  }}
                                >
                                  {Data?.links?.length === i + 1 ? (
                                    <i className="icon-base ri ri-arrow-right-line icon-20px" />
                                  ) : i === 0 ? (
                                    <i className="icon-base ri ri-arrow-left-line icon-20px" />
                                  ) : (
                                    btn?.label
                                  )}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
